import { Card, CardContent, Checkbox } from '@parrot-software/design-system';
import { ChevronRight } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';

import { VerificationBadge } from '@personnel/presentation/components/shared';
import type { EmployeeListItem } from '@personnel/presentation/types';

interface EmployeeSummaryCardProps {
  employee: EmployeeListItem;
  checked: boolean;
  onCheckedChange: (employee: EmployeeListItem, checked: boolean) => void;
  onClick: (employee: EmployeeListItem) => void;
}

/**
 * One user of the list as a card, for the widths where the table would crush its columns.
 *
 * It carries the four values that tell two people apart at a glance and leaves the rest to the
 * detail sheet. The checkbox keeps its click to itself, so ticking a user for a bulk delete never
 * opens their detail.
 *
 * @param {EmployeeSummaryCardProps} props - Component properties
 */
export const EmployeeSummaryCard = ({
  employee,
  checked,
  onCheckedChange,
  onClick,
}: EmployeeSummaryCardProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');

  return (
    <Card
      className="cursor-pointer font-sans"
      data-testid="employee-summary-card"
      onClick={() => onClick(employee)}
      role="button"
      tabIndex={0}
    >
      <CardContent className="flex items-start gap-3 p-4">
        <span className="self-center" onClick={(event) => event.stopPropagation()}>
          <Checkbox
            aria-label={employee.fullName}
            checked={checked}
            onCheckedChange={(isChecked) => onCheckedChange(employee, !!isChecked)}
          />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-medium text-primary">{employee.fullName}</p>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </div>

          <p className="truncate text-sm text-muted-foreground">{employee.email}</p>

          <div className="flex items-center justify-between gap-2">
            <VerificationBadge verificationStatus={employee.verificationStatus} />
            <span className="shrink-0 text-xs text-muted-foreground">
              {t('list.mobile.storesCount', { count: employee.assignedStores ?? 0 })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
